import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import AnalysesTab from "./AnalysesTab";
import DocumentTypesTab from "./DocumentTypesTab";
import GapsTab from "./GapsTab";
import ProcessingTestTab from "./ProcessingTestTab";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Dokumentenanalyse — the catalogue that says what a document *is*.
 *
 * Before a document can be classified, extracted from or expected, there has to be an account of
 * which kinds of document exist, what each one does to the state of an agreement, and which
 * entities and relations of the ontology it carries. That account is written by hand and revised
 * as the model changes; this panel is where it is read and revised.
 *
 * Four tabs, in the order the work goes: the catalogue says what a document can be, the test runs
 * one through the pipeline the catalogue feeds, the record keeps every run that was made, and the
 * gaps say where the documents outran the model. They belong together — what the test gets wrong is
 * edited one tab to the left and takes effect on the next run without anything being deployed,
 * whether that helped is read one tab to the right, and what no amount of editing can fix, because
 * the model has no room for it, is in the last.
 */

interface Properties {}

const TABS = ["documentTypes", "processingTest", "analyses", "gaps"] as const;
type TabKey = (typeof TABS)[number];

export default function DocumentAnalysisPanel(_properties: Properties) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState<TabKey>(() => {
    const requested = new URLSearchParams(window.location.search).get("tab");
    return (TABS as readonly string[]).includes(requested ?? "") ? (requested as TabKey) : "documentTypes";
  });

  /** A single run, pinned by the address — how the processing test hands one over. */
  const [requestedAnalysis, setRequestedAnalysis] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("analysis"),
  );

  /** From the processing test: show me exactly this run, in the tab that lists them. */
  const openAnalysis = (analysisId: string) => {
    setSearchParams(
      () => {
        const next = new URLSearchParams(window.location.search);
        next.set("tab", "analyses");
        next.set("analysis", analysisId);
        return next;
      },
      { replace: false },
    );

    setRequestedAnalysis(analysisId);
    setTab("analyses");
  };

  // The tab belongs in the address, so a colleague can be sent to one. Through the router rather
  // than `history.pushState`: otherwise the router does not know about the new address and
  // overwrites it the next time a table sets its page.
  const changeTab = (value: string) => {
    setSearchParams(
      () => {
        const next = new URLSearchParams(window.location.search);
        next.set("tab", value);
        // Leaving the tab drops the pinned run: coming back should show the whole record, not the
        // one analysis somebody linked to an hour ago.
        next.delete("analysis");
        return next;
      },
      { replace: false },
    );

    setRequestedAnalysis(null);
    setTab(value as TabKey);
  };

  useEffect(() => {
    const onPopState = () => {
      const parameters = new URLSearchParams(window.location.search);
      const requested = parameters.get("tab");

      if ((TABS as readonly string[]).includes(requested ?? "")) setTab(requested as TabKey);
      setRequestedAnalysis(parameters.get("analysis"));
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t("documentAnalysisPanel.title")}</h2>
        <p className="max-w-prose text-sm text-muted-foreground">{t("documentAnalysisPanel.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList>
          <TabsTrigger value="documentTypes">{t("documentAnalysisPanel.tabs.documentTypes")}</TabsTrigger>
          <TabsTrigger value="processingTest">{t("documentAnalysisPanel.tabs.processingTest")}</TabsTrigger>
          <TabsTrigger value="analyses">{t("documentAnalysisPanel.tabs.analyses")}</TabsTrigger>
          <TabsTrigger value="gaps">{t("documentAnalysisPanel.tabs.gaps")}</TabsTrigger>
        </TabsList>

        <TabsContent value="documentTypes" className="mt-4">
          <DocumentTypesTab />
        </TabsContent>

        <TabsContent value="processingTest" className="mt-4">
          <ProcessingTestTab onOpenAnalysis={openAnalysis} />
        </TabsContent>

        <TabsContent value="gaps" className="mt-4">
          <GapsTab />
        </TabsContent>

        <TabsContent value="analyses" className="mt-4">
          {/* Keyed by the requested analysis, so a second link to a different run remounts the tab
              rather than leaving it on the first one. */}
          <AnalysesTab key={requestedAnalysis ?? "all"} initialAnalysisId={requestedAnalysis} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
